import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bot, Settings, MessageSquare, Flag, BarChart2, Plus, Trash2,
  Loader2, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2,
  Sparkles, Cpu, Brain, Map as MapIcon, Save, RefreshCw, Info, Wrench,
  TrendingUp, DollarSign, Clock, Zap,
} from "lucide-react";
import { PlatformLayout } from "./platform-layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

// ── Types ────────────────────────────────────────────────────────────────────
interface HubConfig {
  id?: string;
  surface: string;
  role: string;
  systemPrompt: string | null;
  model: string;
  temperature: number;
  maxResponseLength: number;
  escalationThreshold: number;
  enabled: boolean;
  onboardingCreditsPerMessage?: number | null;
  onboardingCreditGift?: number | null;
  disableMasterContext?: boolean;
}

interface BehaviorRule {
  id: string;
  surface: string;
  role: string;
  rule: string;
  sortOrder: number;
  createdAt: string;
}

interface HubExample {
  id: string;
  surface: string;
  role: string;
  question: string;
  answer: string;
  sortOrder: number;
  createdAt: string;
}

interface Conversation {
  id: string;
  workspaceId: string;
  userId: string;
  surface: string;
  role: string | null;
  sentimentScore: number | null;
  isEscalated: boolean;
  escalationReason: string | null;
  turnCount: number;
  createdAt: string;
  endedAt: string | null;
}

interface ConversationDetail extends Conversation {
  transcript: any[];
  toolsCalled: any[];
}

interface ReviewItem {
  id: string;
  conversationId: string;
  bucket: string;
  surface: string | null;
  role: string | null;
  abstractedContent: string;
  promotedBy: string | null;
  promotedAt: string;
  status: string;
}

interface AnalyticsData {
  totalConversations: number;
  last7Days: number;
  last30Days: number;
  escalatedCount: number;
  escalationRate: number;
  toolGaps: any[];
  recentReviewItems: ReviewItem[];
}

interface SurfaceAnalyticsRow {
  surface: string;
  conversations: number;
  escalatedCount: number;
  escalationRate: number;
  escalationReasonBreakdown: {
    frustration: number;
    complexity: number;
    security: number;
    auto_flagged: number;
    other: number;
  };
  avgSessionDurationMs: number | null;
  avgModelLatencyMs: number | null;
  latencySampleCount: number;
  avgTurns: number | null;
  inputTokens: number;
  outputTokens: number;
  ourCostUsd: number;
  revenueUsd: number;
}

interface RoleAnalyticsRow {
  surface: string;
  role: string;
  conversations: number;
  escalatedCount: number;
  escalationRate: number;
}

interface CostByOperationRow {
  operation: string;
  modelKey: string | null;
  events: number;
  ourCostUsd: number;
  revenueUsd: number;
  marginUsd: number;
  inputTokens: number;
  outputTokens: number;
}

interface BySurfaceAnalytics {
  range: string;
  from: string | null;
  to: string;
  bySurface: SurfaceAnalyticsRow[];
  byRole: RoleAnalyticsRow[];
  costByOperation: CostByOperationRow[];
  totalAiCostUsd: number;
  totalAiRevenueUsd: number;
  marginUsd: number;
  marginPct: number | null;
}

// ── Constants ────────────────────────────────────────────────────────────────
const SURFACES = [
  { id: "assistant",  label: "Clarik AI",      icon: Bot },
  { id: "coach",      label: "Coach Mode",     icon: Brain },
  { id: "tour",       label: "Tour Planner",   icon: MapIcon },
  { id: "onboarding", label: "Onboarding",     icon: Sparkles },
] as const;

const ONBOARDING_STEPS = [
  { id: "org_name",      label: "Organization Name" },
  { id: "talent",        label: "Talent" },
  { id: "offerings",     label: "Offerings" },
  { id: "client_types",  label: "Client Types" },
  { id: "regions",       label: "Regions" },
] as const;

const ROLES = [
  { id: "admin",      label: "Admin" },
  { id: "sales_rep",  label: "Sales" },
  { id: "billing",    label: "Billing" },
  { id: "artist",     label: "Talent" },
  { id: "marketing",  label: "Marketing" },
  { id: "manager",    label: "Manager", disabled: true },
] as const;

// Coach Mode injection variables — substituted into the single shared coach
// prompt at runtime per caller. Mirror of COACH_PROMPT_PLACEHOLDERS on the
// server (server/voice-assistant/system-prompt.ts); keep the two in lockstep.
const COACH_PLACEHOLDERS = [
  { token: "{{role}}",        description: "The caller's role label (e.g. Admin, Sales Rep, Talent)." },
  { token: "{{roleSignals}}", description: "Role-relevant signals block (performance dips, burnout, role vocabulary)." },
  { token: "{{archetype}}",   description: "Tone guidance for the user's chosen coaching archetype (mentor / best friend / drill sergeant / warm parent)." },
  { token: "{{intensity}}",   description: "Tone guidance for the user's chosen intensity (gentle / steady / firm / intense)." },
  { token: "{{styleHint}}",   description: "Style-mirroring hint extracted from the user's own messages." },
  { token: "{{pendingPing}}", description: "The queued initiation nudge for this session, if a cadence ping is pending." },
  { token: "{{coachName}}",   description: "The user's custom name for their coach, if set." },
  { token: "{{aspirations}}", description: "Live snapshot of the user's active aspirations with quantified progress. Appended automatically if omitted." },
] as const;

const MODELS = [
  { id: "claude-sonnet-4-5",    label: "Claude Sonnet 4.5" },
  { id: "claude-haiku-4-5",     label: "Claude Haiku 4.5" },
  { id: "gpt-4o",               label: "GPT-4o" },
  { id: "gpt-4o-mini",          label: "GPT-4o Mini" },
] as const;

const BUCKETS = [
  { id: "knowledge_fact",  label: "Knowledge Fact → KB" },
  { id: "behavior_rule",   label: "Behavior Rule → Role Instructions" },
  { id: "good_example",    label: "Good Example → Q&A Library" },
  { id: "tool_gap",        label: "Tool Gap → Flagged List" },
  { id: "bug_correction",  label: "Bug/Correction → Flagged List" },
] as const;

// ── Helpers ──────────────────────────────────────────────────────────────────
function sentimentBadge(score: number | null) {
  if (score === null) return null;
  if (score >= 0.6) return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Positive</Badge>;
  if (score <= 0.3) return <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">Negative</Badge>;
  return <Badge className="bg-gray-100 text-gray-600 border-gray-200 text-xs">Neutral</Badge>;
}

// ── Configuration Sub-Tab ────────────────────────────────────────────────────
function ConfigurationTab({ surface, lockedRole }: { surface: string; lockedRole?: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const isTourSurface = surface === "tour";
  const isCoachSurface = surface === "coach";
  // Tour Planner and Coach Mode are role-agnostic: one shared config (role="all").
  const isSharedSurface = isTourSurface || isCoachSurface;
  const [selectedRole, setSelectedRole] = useState(lockedRole ?? (isSharedSurface ? "all" : "admin"));
  const [editedPrompt, setEditedPrompt] = useState<string | null>(null);
  const [editedModel, setEditedModel] = useState<string | null>(null);
  const [editedTemp, setEditedTemp] = useState<number | null>(null);
  const [editedMaxLen, setEditedMaxLen] = useState<number | null>(null);
  const [editedThreshold, setEditedThreshold] = useState<number | null>(null);
  const [editedDisableMaster, setEditedDisableMaster] = useState<boolean | null>(null);
  const [newRule, setNewRule] = useState("");
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");

  const { data: configs } = useQuery<{ configs: HubConfig[] }>({
    queryKey: ["/api/platform/ai-hub/configs"],
  });

  const currentConfig = configs?.configs.find(
    (c) => c.surface === surface && c.role === selectedRole,
  );

  // Seeded default prompt (Coach Mode master prompt). Pre-fills the editor when
  // no admin override has been saved, so the rewritten prompt appears + is
  // editable. Only fetched for surfaces that expose a default.
  const { data: defaultPromptData } = useQuery<{ surface: string; systemPrompt: string | null }>({
    queryKey: [`/api/platform/ai-hub/default-prompt/${surface}`],
    enabled: isCoachSurface,
  });

  const { data: rulesData, refetch: refetchRules } = useQuery<{ rules: BehaviorRule[] }>({
    queryKey: [`/api/platform/ai-hub/behavior-rules/${surface}/${selectedRole}`],
  });

  const { data: examplesData, refetch: refetchExamples } = useQuery<{ examples: HubExample[] }>({
    queryKey: [`/api/platform/ai-hub/examples/${surface}/${selectedRole}`],
  });

  const { data: toolOverridesData, refetch: refetchTools } = useQuery<{ overrides: any[] }>({
    queryKey: [`/api/platform/ai-hub/tool-overrides/${surface}/${selectedRole}`],
  });

  const { data: toolRegistryData } = useQuery<{ tools: Array<{ name: string; description: string; surface: string; isWrite: boolean; allowedRoles: string[]; category?: string }>; categoryOrder?: string[] }>({
    queryKey: ["/api/platform/ai-hub/tool-registry"],
  });

  // Build a set of explicitly-disabled tool names for this surface+role
  const disabledTools = new Set(
    (toolOverridesData?.overrides ?? [])
      .filter((o: any) => o.enabled === false)
      .map((o: any) => o.toolName as string),
  );

  // Filter registry to tools relevant to this surface (or assistant for cross-surface)
  const relevantTools = (toolRegistryData?.tools ?? []).filter(
    (t) => t.surface === surface || (surface === "assistant" && t.surface === "assistant"),
  );

  // Group the (otherwise flat) tool list by category, then order the categories
  // by the canonical order the server defines (categoryOrder). Categories not in
  // that list (e.g. "Other") fall to the end.
  const toolCategories: Array<{ category: string; tools: typeof relevantTools }> = [];
  {
    const byCategory = new Map<string, typeof relevantTools>();
    for (const t of relevantTools) {
      const cat = t.category ?? "Other";
      if (!byCategory.has(cat)) byCategory.set(cat, []);
      byCategory.get(cat)!.push(t);
    }
    const order = toolRegistryData?.categoryOrder ?? [];
    const rank = (cat: string) => {
      const i = order.indexOf(cat);
      return i === -1 ? order.length + 1 : i;
    };
    for (const cat of Array.from(byCategory.keys()).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))) {
      toolCategories.push({ category: cat, tools: byCategory.get(cat)! });
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PUT", `/api/platform/ai-hub/configs/${surface}/${selectedRole}`, {
        systemPrompt: editedPrompt ?? currentConfig?.systemPrompt ?? null,
        model: editedModel ?? currentConfig?.model ?? "claude-sonnet-4-5",
        temperature: editedTemp ?? currentConfig?.temperature ?? 0.7,
        maxResponseLength: editedMaxLen ?? currentConfig?.maxResponseLength ?? 2000,
        escalationThreshold: editedThreshold ?? currentConfig?.escalationThreshold ?? 0.3,
        enabled: currentConfig?.enabled ?? true,
        disableMasterContext: editedDisableMaster ?? currentConfig?.disableMasterContext ?? false,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/ai-hub/configs"] });
      setEditedPrompt(null);
      setEditedModel(null);
      setEditedTemp(null);
      setEditedMaxLen(null);
      setEditedThreshold(null);
      setEditedDisableMaster(null);
      toast({ title: "Configuration saved" });
    },
    onError: (err: any) => {
      toast({ title: "Save failed", description: err?.message, variant: "destructive" });
    },
  });

  const addRuleMutation = useMutation({
    mutationFn: async (rule: string) => {
      await apiRequest("POST", "/api/platform/ai-hub/behavior-rules", { surface, role: selectedRole, rule });
    },
    onSuccess: () => {
      refetchRules();
      setNewRule("");
      toast({ title: "Rule added" });
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (id: string) =>
      apiRequest("DELETE", `/api/platform/ai-hub/behavior-rules/${id}`),
    onSuccess: () => refetchRules(),
  });

  const addExampleMutation = useMutation({
    mutationFn: async ({ question, answer }: { question: string; answer: string }) => {
      await apiRequest("POST", "/api/platform/ai-hub/examples", { surface, role: selectedRole, question, answer });
    },
    onSuccess: () => {
      refetchExamples();
      setNewQuestion("");
      setNewAnswer("");
      toast({ title: "Example added" });
    },
  });

  const deleteExampleMutation = useMutation({
    mutationFn: async (id: string) =>
      apiRequest("DELETE", `/api/platform/ai-hub/examples/${id}`),
    onSuccess: () => refetchExamples(),
  });

  const toggleToolMutation = useMutation({
    mutationFn: async ({ toolName, enabled }: { toolName: string; enabled: boolean }) => {
      await apiRequest("PUT", `/api/platform/ai-hub/tool-overrides/${surface}/${selectedRole}`, { toolName, enabled });
    },
    onSuccess: () => refetchTools(),
  });

  const bulkToolMutation = useMutation({
    mutationFn: async ({ toolNames, enabled }: { toolNames: string[]; enabled: boolean }) => {
      await apiRequest("PUT", `/api/platform/ai-hub/tool-overrides/${surface}/${selectedRole}/bulk`, { toolNames, enabled });
    },
    onSuccess: () => refetchTools(),
    onError: (err: any) => {
      toast({ title: "Bulk update failed", description: err?.message, variant: "destructive" });
    },
  });

  const hasChanges = editedPrompt !== null || editedModel !== null || editedTemp !== null || editedMaxLen !== null || editedThreshold !== null || editedDisableMaster !== null;

  const promptFallback = isCoachSurface ? (defaultPromptData?.systemPrompt ?? "") : "";
  const currentPrompt = editedPrompt ?? currentConfig?.systemPrompt ?? promptFallback;
  const currentModel = editedModel ?? currentConfig?.model ?? "claude-sonnet-4-5";
  const currentTemp = editedTemp ?? currentConfig?.temperature ?? 0.7;
  const currentMaxLen = editedMaxLen ?? currentConfig?.maxResponseLength ?? 2000;
  const currentThreshold = editedThreshold ?? currentConfig?.escalationThreshold ?? 0.3;
  const currentDisableMaster = editedDisableMaster ?? currentConfig?.disableMasterContext ?? false;

  return (
    <div className="space-y-6">
      {/* Coach Mode — read-only user-facing settings reference */}
      {surface === "coach" && (
        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-2">
              <Brain className="h-4 w-4" />
              User-Facing Coach Settings (read-only reference)
            </CardTitle>
            <CardDescription className="text-xs">
              These are the settings users configure in their Notification Settings → Coach card.
              They are not editable here — this context helps you write system prompts that align with user preferences.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Cadence</p>
                {["never", "after_each_event", "daily", "weekly", "biweekly", "monthly"].map((c) => (
                  <div key={c} className="text-xs px-2 py-1 bg-purple-50 dark:bg-purple-950/30 rounded text-purple-800 dark:text-purple-200 font-mono">{c}</div>
                ))}
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Archetype</p>
                {[
                  { id: "mentor", label: "Mentor — guiding, instructional" },
                  { id: "coach", label: "Coach — action-oriented, direct" },
                  { id: "advisor", label: "Advisor — strategic, analytical" },
                  { id: "partner", label: "Partner — collaborative, peer-level" },
                ].map((a) => (
                  <div key={a.id} className="text-xs px-2 py-1 bg-purple-50 dark:bg-purple-950/30 rounded text-purple-800 dark:text-purple-200">{a.label}</div>
                ))}
              </div>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Intensity</p>
                {[
                  { id: "gentle", label: "Gentle — empathic, low pressure" },
                  { id: "moderate", label: "Moderate — balanced, encouraging" },
                  { id: "direct", label: "Direct — blunt, no-fluff" },
                ].map((i) => (
                  <div key={i.id} className="text-xs px-2 py-1 bg-purple-50 dark:bg-purple-950/30 rounded text-purple-800 dark:text-purple-200">{i.label}</div>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-3 leading-snug">
              The Coach system prompt automatically receives a <code className="font-mono bg-muted px-0.5 rounded">styleHint</code> block
              (EWMA-derived from real user turns) and a <code className="font-mono bg-muted px-0.5 rounded">pendingPing</code> when
              an initiation cadence fires. Write your system prompt to <em>use</em> these variables — they are injected at runtime.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Coach Mode — single shared config banner + placeholder reference */}
      {isCoachSurface && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="py-3 px-4 space-y-3">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
                <span className="font-semibold">Coach Mode uses a single shared prompt.</span> The same system prompt, guardrails, and examples apply to every caller regardless of role — there is no per-role tab. Reference the injection variables below anywhere in your prompt and they're filled in at runtime per caller. If you omit them, the role/persona context is appended automatically so nothing is lost.
              </p>
            </div>
            <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-white/60 dark:bg-blue-950/30 p-3" data-testid="panel-coach-placeholders">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300 mb-2">Available injection variables</p>
              <dl className="space-y-1.5">
                {COACH_PLACEHOLDERS.map((p) => (
                  <div key={p.token} className="grid grid-cols-[auto_1fr] gap-x-2 items-baseline" data-testid={`placeholder-${p.token.replace(/[{}]/g, "")}`}>
                    <dt><code className="font-mono text-[11px] bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 px-1 py-0.5 rounded whitespace-nowrap">{p.token}</code></dt>
                    <dd className="text-[11px] text-blue-800/90 dark:text-blue-200/90 leading-snug">{p.description}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tour Planner — single shared config banner */}
      {isTourSurface && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="py-3 px-4">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
                <span className="font-semibold">Tour Planner uses a single shared config.</span> The same system prompt, guardrails, and examples apply to every caller. The only runtime distinction is whether the caller is the talent themselves — that flag is injected automatically as a{" "}
                <code className="font-mono bg-blue-100 dark:bg-blue-900/50 px-0.5 rounded">## Caller Context</code> block containing{" "}
                <code className="font-mono bg-blue-100 dark:bg-blue-900/50 px-0.5 rounded">Is talent: yes / no</code>.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role selector — hidden when lockedRole is set (e.g. onboarding steps) or for shared surfaces (tour/coach) */}
      {!lockedRole && !isSharedSurface && (
        <div className="flex items-center gap-2 flex-wrap">
          {ROLES.map((r) => (
            <Button
              key={r.id}
              variant={selectedRole === r.id ? "default" : "outline"}
              size="sm"
              disabled={"disabled" in r && r.disabled}
              onClick={() => {
                setSelectedRole(r.id);
                setEditedPrompt(null);
                setEditedModel(null);
                setEditedTemp(null);
                setEditedMaxLen(null);
                setEditedThreshold(null);
              }}
              data-testid={`btn-role-${r.id}`}
            >
              {r.label}
              {"disabled" in r && r.disabled && (
                <Badge className="ml-1.5 bg-gray-200 text-gray-500 text-[10px] py-0">Coming Soon</Badge>
              )}
            </Button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: system prompt + guardrails */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center justify-between">
                System Prompt
                {hasChanges && (
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending}
                    data-testid="btn-save-config"
                  >
                    {saveMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    Save changes
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <VoiceToTextTextarea
                value={currentPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                placeholder={isSharedSurface ? "Write the system prompt for all callers…" : `Write the system prompt for the ${ROLES.find((r) => r.id === selectedRole)?.label ?? selectedRole} role…`}
                className="font-mono text-xs min-h-[200px]"
                data-testid="textarea-system-prompt"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Guardrails</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Model</Label>
                  <Select value={currentModel} onValueChange={(v) => setEditedModel(v)}>
                    <SelectTrigger className="h-8 text-sm" data-testid="select-model">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODELS.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">
                    Max Response Length
                  </Label>
                  <Input
                    type="number"
                    value={currentMaxLen}
                    onChange={(e) => setEditedMaxLen(parseInt(e.target.value) || 2000)}
                    min={100}
                    max={16000}
                    step={100}
                    className="h-8 text-sm"
                    data-testid="input-max-length"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">
                  Temperature / Creativity — {currentTemp.toFixed(2)}
                </Label>
                <Slider
                  value={[currentTemp]}
                  min={0}
                  max={1}
                  step={0.05}
                  onValueChange={([v]) => setEditedTemp(v)}
                  className="w-full"
                  data-testid="slider-temperature"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                  <span>Precise (0)</span>
                  <span>Creative (1)</span>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">
                  Escalation Threshold — {currentThreshold.toFixed(2)}
                </Label>
                <Slider
                  value={[currentThreshold]}
                  min={0}
                  max={1}
                  step={0.05}
                  onValueChange={([v]) => setEditedThreshold(v)}
                  className="w-full"
                  data-testid="slider-escalation-threshold"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                  <span>Rarely escalate (0)</span>
                  <span>Always escalate (1)</span>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <div className="space-y-0.5 pr-4">
                  <Label className="text-xs font-medium">Disable Master Context</Label>
                  <p className="text-[10px] text-muted-foreground">
                    Exclude the platform-wide Master Context (Voice &amp; Tone + general library) from this surface/role.
                  </p>
                </div>
                <Switch
                  checked={currentDisableMaster}
                  onCheckedChange={(v) => setEditedDisableMaster(v)}
                  data-testid="switch-disable-master-context"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column: rules + examples */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Behavior Rules</CardTitle>
              <CardDescription className="text-xs">
                E.g. "never quote a rate without checking the rate sheet"
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {rulesData?.rules.map((rule) => (
                <div key={rule.id} className="flex items-start gap-2 group text-xs bg-gray-50 dark:bg-gray-900 rounded px-2 py-1.5">
                  <span className="flex-1 leading-snug">{rule.rule}</span>
                  <button
                    onClick={() => deleteRuleMutation.mutate(rule.id)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 shrink-0"
                    data-testid={`btn-delete-rule-${rule.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {(!rulesData?.rules || rulesData.rules.length === 0) && (
                <p className="text-xs text-muted-foreground">No rules yet.</p>
              )}
              <div className="flex gap-2 pt-1">
                <Input
                  value={newRule}
                  onChange={(e) => setNewRule(e.target.value)}
                  placeholder="Add a rule…"
                  className="text-xs h-7"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newRule.trim()) addRuleMutation.mutate(newRule.trim());
                  }}
                  data-testid="input-new-rule"
                />
                <Button
                  size="sm"
                  className="h-7 px-2"
                  disabled={!newRule.trim() || addRuleMutation.isPending}
                  onClick={() => addRuleMutation.mutate(newRule.trim())}
                  data-testid="btn-add-rule"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Q&A Examples</CardTitle>
              <CardDescription className="text-xs">Few-shot examples to model good behavior</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {examplesData?.examples.map((ex) => (
                <div key={ex.id} className="group text-xs bg-gray-50 dark:bg-gray-900 rounded p-2 space-y-1">
                  <div className="flex items-start gap-1.5">
                    <span className="font-medium text-blue-600 shrink-0">Q:</span>
                    <span className="flex-1">{ex.question}</span>
                    <button
                      onClick={() => deleteExampleMutation.mutate(ex.id)}
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 shrink-0"
                      data-testid={`btn-delete-example-${ex.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <span className="font-medium text-green-600 shrink-0">A:</span>
                    <span className="flex-1 text-muted-foreground">{ex.answer}</span>
                  </div>
                </div>
              ))}
              {(!examplesData?.examples || examplesData.examples.length === 0) && (
                <p className="text-xs text-muted-foreground">No examples yet.</p>
              )}
              <div className="space-y-1.5 pt-1">
                <Input
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  placeholder="Question…"
                  className="text-xs h-7"
                  data-testid="input-new-question"
                />
                <Input
                  value={newAnswer}
                  onChange={(e) => setNewAnswer(e.target.value)}
                  placeholder="Answer…"
                  className="text-xs h-7"
                  data-testid="input-new-answer"
                />
                <Button
                  size="sm"
                  className="w-full h-7 text-xs gap-1"
                  disabled={!newQuestion.trim() || !newAnswer.trim() || addExampleMutation.isPending}
                  onClick={() => addExampleMutation.mutate({ question: newQuestion.trim(), answer: newAnswer.trim() })}
                  data-testid="btn-add-example"
                >
                  <Plus className="h-3 w-3" /> Add example
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tool Access Checklist */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Wrench className="h-4 w-4 text-muted-foreground" />
            Tool Access for {ROLES.find((r) => r.id === selectedRole)?.label ?? selectedRole}
          </CardTitle>
          <CardDescription className="text-xs">
            Toggle individual tools on or off for this surface + role. Disabled tools are hidden from the AI entirely — it won't even attempt to call them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!toolRegistryData && (
            <p className="text-xs text-muted-foreground">Loading tool registry…</p>
          )}
          {toolRegistryData && relevantTools.length === 0 && (
            <p className="text-xs text-muted-foreground">No tools available for this surface.</p>
          )}
          {relevantTools.length > 0 && (
            <div className="space-y-5">
              {toolCategories.map(({ category, tools }) => {
                const names = tools.map((t) => t.name);
                const enabledCount = names.filter((n) => !disabledTools.has(n)).length;
                const allEnabled = enabledCount === names.length;
                const noneEnabled = enabledCount === 0;
                return (
                  <div key={category} data-testid={`tool-category-${category}`}>
                    <div className="flex items-center justify-between gap-2 mb-2 pb-1.5 border-b border-border">
                      <div className="flex items-center gap-2 min-w-0">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {category}
                        </h4>
                        <span className="text-[10px] text-muted-foreground/70 shrink-0">
                          {enabledCount}/{names.length}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px]"
                          disabled={allEnabled || bulkToolMutation.isPending}
                          onClick={() => bulkToolMutation.mutate({ toolNames: names, enabled: true })}
                          data-testid={`button-select-all-${category}`}
                        >
                          Select all
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px]"
                          disabled={noneEnabled || bulkToolMutation.isPending}
                          onClick={() => bulkToolMutation.mutate({ toolNames: names, enabled: false })}
                          data-testid={`button-unselect-all-${category}`}
                        >
                          Unselect all
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {tools.map((tool) => {
                        const isEnabled = !disabledTools.has(tool.name);
                        return (
                          <div
                            key={tool.name}
                            className={`flex items-start gap-2 rounded border px-2.5 py-2 text-xs cursor-pointer select-none transition-colors ${
                              isEnabled
                                ? "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"
                                : "bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 opacity-60"
                            }`}
                            onClick={() =>
                              toggleToolMutation.mutate({ toolName: tool.name, enabled: !isEnabled })
                            }
                            data-testid={`toggle-tool-${tool.name}`}
                          >
                            <div className={`mt-0.5 h-3 w-3 rounded-sm border flex items-center justify-center shrink-0 ${
                              isEnabled ? "bg-blue-600 border-blue-600" : "border-gray-400"
                            }`}>
                              {isEnabled && (
                                <svg viewBox="0 0 10 8" fill="none" className="h-2 w-2">
                                  <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium leading-snug break-all">{tool.name}</p>
                              {tool.isWrite && (
                                <Badge className="mt-0.5 text-[9px] py-0 px-1 bg-amber-100 text-amber-700 border-amber-200">write</Badge>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Escalation reason pill — shared between Conversations, Review Queue ──────
function EscalationReasonPill({ reason }: { reason: string }) {
  const cfg: Record<string, { label: string; className: string }> = {
    frustration:  { label: "Frustration",  className: "bg-red-100 text-red-700 border-red-200" },
    complexity:   { label: "Complexity",   className: "bg-amber-100 text-amber-700 border-amber-200" },
    security:     { label: "Security",     className: "bg-purple-100 text-purple-700 border-purple-200" },
    auto_flagged: { label: "Auto-flagged", className: "bg-blue-100 text-blue-700 border-blue-200" },
  };
  const c = cfg[reason] ?? { label: reason, className: "bg-gray-100 text-gray-600 border-gray-200" };
  return (
    <Badge className={`text-[10px] py-0 px-1.5 border ${c.className} flex items-center gap-0.5`}>
      <AlertTriangle className="h-2.5 w-2.5" />
      {c.label}
    </Badge>
  );
}

// ── Conversations Sub-Tab ────────────────────────────────────────────────────
// Simple per-turn sentiment heuristic (matches server-side FRUSTRATION_RE)
const FRUSTRATION_RE_CLIENT = /not what i meant|that('s| is) not|that isn'?t right|try again|still not|doesn'?t help|not helpful|no no|ugh|come on|wrong answer|incorrect|misunderstood|you misunderstood/i;
function turnSentiment(text: string): "frustrated" | "neutral" {
  return FRUSTRATION_RE_CLIENT.test(text) ? "frustrated" : "neutral";
}

function ConversationsTab({ surface }: { surface: string }) {
  const [filters, setFilters] = useState({
    role: "", escalatedOnly: false, dateFrom: "", dateTo: "",
    workspaceId: "", sentimentRange: "all",
  });
  const [selectedConv, setSelectedConv] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const limit = 50;

  const params = new URLSearchParams({ surface, limit: String(limit), offset: String(page * limit) });
  if (filters.role) params.set("role", filters.role);
  if (filters.escalatedOnly) params.set("escalatedOnly", "true");
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
  if (filters.workspaceId.trim()) params.set("workspaceId", filters.workspaceId.trim());
  if (filters.sentimentRange === "positive") params.set("minSentiment", "0.6");
  if (filters.sentimentRange === "neutral") { params.set("minSentiment", "0.35"); params.set("maxSentiment", "0.6"); }
  if (filters.sentimentRange === "negative") params.set("maxSentiment", "0.35");

  const { data, isLoading } = useQuery<{ conversations: Conversation[]; total: number }>({
    queryKey: [`/api/platform/ai-hub/conversations?${params.toString()}`],
  });

  const { data: detail } = useQuery<ConversationDetail>({
    queryKey: [`/api/platform/ai-hub/conversations/${selectedConv}`],
    enabled: !!selectedConv,
  });

  return (
    <div className="space-y-4">
      {/* Filters — row 1: role + sentiment + escalated */}
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={filters.role || "all"} onValueChange={(v) => setFilters(f => ({ ...f, role: v === "all" ? "" : v }))}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="select-filter-role">
            <SelectValue placeholder="All roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLES.filter(r => !("disabled" in r && r.disabled)).map((r) => (
              <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.sentimentRange} onValueChange={(v) => setFilters(f => ({ ...f, sentimentRange: v }))}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="select-filter-sentiment">
            <SelectValue placeholder="Sentiment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sentiment</SelectItem>
            <SelectItem value="positive">Positive (≥0.6)</SelectItem>
            <SelectItem value="neutral">Neutral</SelectItem>
            <SelectItem value="negative">Negative (≤0.35)</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <Switch
            checked={filters.escalatedOnly}
            onCheckedChange={(v) => setFilters(f => ({ ...f, escalatedOnly: v }))}
            data-testid="switch-escalated-only"
          />
          <span className="text-xs text-muted-foreground">Escalated only</span>
        </div>
      </div>
      {/* Filters — row 2: workspace + date range */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          value={filters.workspaceId}
          onChange={(e) => setFilters(f => ({ ...f, workspaceId: e.target.value }))}
          placeholder="Workspace ID…"
          className="h-8 w-56 text-xs font-mono"
          data-testid="input-filter-workspace"
        />
        <Input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
          className="h-8 w-36 text-xs"
          data-testid="input-date-from"
        />
        <span className="text-xs text-muted-foreground">→</span>
        <Input
          type="date"
          value={filters.dateTo}
          onChange={(e) => setFilters(f => ({ ...f, dateTo: e.target.value }))}
          className="h-8 w-36 text-xs"
          data-testid="input-date-to"
        />
        {data?.total !== undefined && (
          <span className="text-xs text-muted-foreground ml-auto">{data.total} conversation{data.total !== 1 ? "s" : ""}</span>
        )}
      </div>

      {/* Conversation list */}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Workspace</TableHead>
              <TableHead className="text-xs">{surface === "onboarding" ? "Step" : "Role"}</TableHead>
              <TableHead className="text-xs">Turns</TableHead>
              <TableHead className="text-xs">Sentiment</TableHead>
              <TableHead className="text-xs">Date</TableHead>
              <TableHead className="text-xs">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (!data?.conversations || data.conversations.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-xs text-muted-foreground">
                  No conversations yet.
                </TableCell>
              </TableRow>
            )}
            {data?.conversations.map((conv) => (
              <TableRow
                key={conv.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => setSelectedConv(conv.id === selectedConv ? null : conv.id)}
                data-testid={`row-conversation-${conv.id}`}
              >
                <TableCell className="text-xs font-mono">{conv.workspaceId.slice(0, 8)}…</TableCell>
                <TableCell className="text-xs capitalize">
                  {surface === "onboarding"
                    ? ONBOARDING_STEPS.find((s) => s.id === conv.role)?.label ?? conv.role ?? "—"
                    : conv.role ?? "—"}
                </TableCell>
                <TableCell className="text-xs">{conv.turnCount}</TableCell>
                <TableCell>{sentimentBadge(conv.sentimentScore)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {format(new Date(conv.createdAt), "MMM d, HH:mm")}
                </TableCell>
                <TableCell>
                  {conv.isEscalated ? (
                    <Badge className="bg-red-100 text-red-700 border-red-200 text-xs flex items-center gap-1 w-fit">
                      <AlertTriangle className="h-3 w-3" /> Escalated
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-600 border-gray-200 text-xs">Normal</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Transcript drawer */}
      {selectedConv && detail && (
        <Card className="border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <span>Transcript — {detail.id.slice(0, 8)}…</span>
                {detail.isEscalated && detail.escalationReason && (
                  <EscalationReasonPill reason={detail.escalationReason} />
                )}
                {detail.isEscalated && !detail.escalationReason && (
                  <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] py-0 px-1.5 border flex items-center gap-0.5">
                    <AlertTriangle className="h-2.5 w-2.5" /> Escalated
                  </Badge>
                )}
              </div>
              <button onClick={() => setSelectedConv(null)} className="text-muted-foreground hover:text-foreground text-xs shrink-0">
                Close
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-80 overflow-y-auto">
            {Array.isArray(detail.transcript) && detail.transcript.length === 0 && (
              <p className="text-xs text-muted-foreground">No transcript recorded.</p>
            )}
            {Array.isArray(detail.transcript) && detail.transcript.map((turn: any, i: number) => {
              const text = turn.content ?? turn.text ?? "";
              const isFrustrated = turn.role === "user" && turnSentiment(text) === "frustrated";
              return (
                <div
                  key={i}
                  className={`text-xs px-3 py-2 rounded ${
                    turn.role === "user"
                      ? isFrustrated
                        ? "bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 ml-4"
                        : "bg-blue-50 dark:bg-blue-950 ml-4"
                      : "bg-gray-50 dark:bg-gray-900 mr-4"
                  }`}
                  data-testid={`transcript-turn-${i}`}
                >
                  <span className="font-medium capitalize">{turn.role}: </span>
                  {text || JSON.stringify(turn)}
                  {isFrustrated && (
                    <span className="ml-2 text-[10px] text-red-500 font-medium">● frustrated</span>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {data && data.total > limit && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            {page * limit + 1}–{Math.min((page + 1) * limit, data.total)} of {data.total}
          </span>
          <Button variant="outline" size="sm" disabled={(page + 1) * limit >= data.total} onClick={() => setPage(p => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Review Queue Sub-Tab ─────────────────────────────────────────────────────
function ReviewQueueTab({ surface }: { surface: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [promoteConvId, setPromoteConvId] = useState<string | null>(null);
  const [promoteBucket, setPromoteBucket] = useState("knowledge_fact");
  const [promoteContent, setPromoteContent] = useState("");
  const [promoteRole, setPromoteRole] = useState("admin");

  const { data, isLoading } = useQuery<{ items: ReviewItem[] }>({
    queryKey: [`/api/platform/ai-hub/review-queue?surface=${surface}`],
  });

  const { data: escalated, isLoading: escalatedLoading } = useQuery<{
    conversations: Conversation[];
    total: number;
  }>({
    queryKey: [`/api/platform/ai-hub/conversations?surface=${surface}&escalatedOnly=true&limit=20`],
  });

  const promoteMutation = useMutation({
    mutationFn: async () => {
      if (!promoteConvId) throw new Error("No conversation selected");
      await apiRequest("POST", "/api/platform/ai-hub/review-queue", {
        conversationId: promoteConvId,
        bucket: promoteBucket,
        surface,
        role: promoteRole,
        abstractedContent: promoteContent,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`/api/platform/ai-hub/review-queue?surface=${surface}`] });
      setPromoteConvId(null);
      setPromoteContent("");
      toast({ title: "Promoted to review queue" });
    },
    onError: (err: any) => {
      toast({ title: "Promotion failed", description: err?.message, variant: "destructive" });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await apiRequest("PUT", `/api/platform/ai-hub/review-queue/${id}`, { status });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`/api/platform/ai-hub/review-queue?surface=${surface}`] });
    },
  });

  return (
    <div className="space-y-6">
      {/* Flagged conversations awaiting review */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Escalated Conversations
          </CardTitle>
          <CardDescription className="text-xs">
            Sessions that crossed the escalation threshold. Select one to promote to the review queue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {escalatedLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!escalatedLoading && (!escalated?.conversations || escalated.conversations.length === 0) && (
            <p className="text-xs text-muted-foreground">No escalated conversations.</p>
          )}
          <div className="space-y-1">
            {escalated?.conversations.map((conv) => (
              <div
                key={conv.id}
                className={`flex items-center gap-2 flex-wrap px-3 py-2 rounded text-xs cursor-pointer border ${
                  promoteConvId === conv.id
                    ? "border-blue-400 bg-blue-50 dark:bg-blue-950"
                    : "border-transparent hover:bg-muted/50"
                }`}
                onClick={() => setPromoteConvId(conv.id === promoteConvId ? null : conv.id)}
                data-testid={`row-escalated-${conv.id}`}
              >
                <span className="font-mono shrink-0">{conv.id.slice(0, 8)}…</span>
                <span className="text-muted-foreground capitalize shrink-0">{conv.role ?? "—"}</span>
                <span className="text-muted-foreground shrink-0">{format(new Date(conv.createdAt), "MMM d")}</span>
                {sentimentBadge(conv.sentimentScore)}
                {conv.escalationReason && (
                  <EscalationReasonPill reason={conv.escalationReason} />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Promote form */}
      {promoteConvId && (
        <Card className="border-blue-300">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Promote to Review Queue</CardTitle>
            <CardDescription className="text-xs text-amber-600">
              <Info className="h-3 w-3 inline mr-1" />
              You must abstract this content before promoting. Never include raw client names, rates, or identifying details — only human-approved, identity-agnostic principles.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Bucket</Label>
                <Select value={promoteBucket} onValueChange={setPromoteBucket}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-promote-bucket">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUCKETS.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Target Role</Label>
                <Select value={promoteRole} onValueChange={setPromoteRole}>
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="select-promote-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.filter(r => !("disabled" in r && r.disabled)).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Abstracted Content</Label>
              <VoiceToTextTextarea
                value={promoteContent}
                onChange={(e) => setPromoteContent(e.target.value)}
                placeholder="Write an abstracted, identity-agnostic version of the insight…"
                className="text-xs mt-1 min-h-[80px]"
                data-testid="textarea-promote-content"
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="h-7 text-xs"
                disabled={!promoteContent.trim() || promoteMutation.isPending}
                onClick={() => promoteMutation.mutate()}
                data-testid="btn-promote"
              >
                {promoteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Promote
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setPromoteConvId(null); setPromoteContent(""); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing review items */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Review Queue Items</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!isLoading && (!data?.items || data.items.length === 0) && (
            <p className="text-xs text-muted-foreground">No items in the queue yet.</p>
          )}
          <div className="space-y-2">
            {data?.items.map((item) => (
              <div key={item.id} className="border rounded p-3 space-y-1" data-testid={`review-item-${item.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge className="text-xs capitalize">{item.bucket.replace(/_/g, " ")}</Badge>
                    {item.role && <Badge variant="outline" className="text-xs capitalize">{item.role}</Badge>}
                    <Badge
                      className={`text-xs ${
                        item.status === "applied" ? "bg-green-100 text-green-700" :
                        item.status === "dismissed" ? "bg-gray-100 text-gray-500" :
                        "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {item.status}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    {item.status === "pending" && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-xs px-2"
                          onClick={() => updateStatusMutation.mutate({ id: item.id, status: "applied" })}
                          data-testid={`btn-apply-${item.id}`}
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1 text-green-600" /> Apply
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-xs px-2"
                          onClick={() => updateStatusMutation.mutate({ id: item.id, status: "dismissed" })}
                          data-testid={`btn-dismiss-${item.id}`}
                        >
                          Dismiss
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground leading-snug">{item.abstractedContent}</p>
                <p className="text-[10px] text-muted-foreground">{format(new Date(item.promotedAt), "MMM d, yyyy")}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Helpers for Analytics tab ────────────────────────────────────────────────
function fmtDuration(ms: number | null): string {
  if (ms == null || ms <= 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}
function fmtUsd(n: number): string {
  return `$${n.toFixed(4)}`;
}
function escalationRateColor(rate: number): string {
  if (rate >= 0.2) return "text-red-600";
  if (rate >= 0.1) return "text-amber-600";
  return "text-green-600";
}

function EscalationReasonBadges({ breakdown }: { breakdown: SurfaceAnalyticsRow["escalationReasonBreakdown"] }) {
  const entries = [
    { key: "frustration",  label: "Frustration",  color: "bg-red-100 text-red-700 border-red-200" },
    { key: "complexity",   label: "Complexity",   color: "bg-amber-100 text-amber-700 border-amber-200" },
    { key: "security",     label: "Security",     color: "bg-purple-100 text-purple-700 border-purple-200" },
    { key: "auto_flagged", label: "Auto-flagged", color: "bg-blue-100 text-blue-700 border-blue-200" },
    { key: "other",        label: "Other",        color: "bg-gray-100 text-gray-600 border-gray-200" },
  ] as const;
  const total = Object.values(breakdown).reduce((s, v) => s + v, 0);
  if (total === 0) return <span className="text-xs text-muted-foreground">none</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(({ key, label, color }) => {
        const n = breakdown[key];
        if (!n) return null;
        return (
          <Badge key={key} className={`text-[10px] py-0 px-1.5 border ${color}`}>
            {label}: {n}
          </Badge>
        );
      })}
    </div>
  );
}

// ── Analytics Sub-Tab ────────────────────────────────────────────────────────
function AnalyticsTab({ surface }: { surface: string }) {
  const [range, setRange] = useState<"7d" | "30d" | "90d" | "all">("30d");

  const { data, isLoading } = useQuery<AnalyticsData>({
    queryKey: [`/api/platform/ai-hub/analytics?surface=${surface}`],
    refetchInterval: 60_000,
  });

  const { data: bySurfaceData, isLoading: bySurfaceLoading } = useQuery<BySurfaceAnalytics>({
    queryKey: [`/api/platform/ai-hub/analytics/by-surface?range=${range}`],
    refetchInterval: 60_000,
  });

  const nightlyLastRun = (data as any)?.nightlyJobLastRun;
  const toolUsageByRole: Array<{ role: string; topTools: Array<{ name: string; count: number }> }> = (data as any)?.toolUsageByRole ?? [];
  const sessionDayTrend: Array<{ day: string; sessions: number }> = (data as any)?.sessionDayTrend ?? [];
  const noAnswerRate: number = (data as any)?.noAnswerRate ?? 0;

  // Filter bySurface data to the current surface (if not "all"), otherwise show all
  const surfaceRow = bySurfaceData?.bySurface.find((r) => r.surface === surface);
  const surfaceRows = surface
    ? (surfaceRow ? [surfaceRow] : [])
    : (bySurfaceData?.bySurface ?? []);

  // Role breakdown filtered to this surface
  const roleRows = (bySurfaceData?.byRole ?? []).filter((r) => !surface || r.surface === surface);

  const RANGE_LABELS: Record<string, string> = {
    "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days", "all": "All time",
  };

  return (
    <div className="space-y-6">
      {/* Time-range selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground font-medium">Time range:</span>
        {(["7d", "30d", "90d", "all"] as const).map((r) => (
          <Button
            key={r}
            variant={range === r ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs px-3"
            onClick={() => setRange(r)}
            data-testid={`btn-range-${r}`}
          >
            {RANGE_LABELS[r]}
          </Button>
        ))}
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total sessions", value: data?.totalConversations ?? 0, color: "text-blue-600" },
          { label: "Last 7 days", value: data?.last7Days ?? 0, color: "text-green-600" },
          { label: "Escalation rate", value: `${((data?.escalationRate ?? 0) * 100).toFixed(1)}%`, color: data && data.escalationRate > 0.2 ? "text-red-600" : "text-gray-700" },
          { label: "No-tool rate (30d)", value: `${(noAnswerRate * 100).toFixed(1)}%`, color: noAnswerRate > 0.3 ? "text-amber-600" : "text-gray-700" },
        ].map((m) => (
          <Card key={m.label}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{m.label}</p>
              <p className={`text-2xl font-bold mt-1 ${m.color}`}>{m.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Per-surface breakdown ────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Zap className="h-4 w-4 text-blue-500" />
            Per-Surface Metrics — {RANGE_LABELS[range]}
          </CardTitle>
          <CardDescription className="text-xs">
            Session counts, escalation rates, token usage, attributed cost, and avg model response latency per AI surface.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {bySurfaceLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!bySurfaceLoading && surfaceRows.length === 0 && (
            <p className="text-xs text-muted-foreground">No conversations in this period.</p>
          )}
          {surfaceRows.length > 0 && (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Surface</TableHead>
                    <TableHead className="text-xs text-right">Sessions</TableHead>
                    <TableHead className="text-xs text-right">Escalated</TableHead>
                    <TableHead className="text-xs text-right">Esc. Rate</TableHead>
                    <TableHead className="text-xs">Escalation Reasons</TableHead>
                    <TableHead className="text-xs text-right">Tokens In</TableHead>
                    <TableHead className="text-xs text-right">Tokens Out</TableHead>
                    <TableHead className="text-xs text-right">Cost</TableHead>
                    <TableHead className="text-xs text-right">Avg Latency</TableHead>
                    <TableHead className="text-xs text-right">Avg Turns</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {surfaceRows.map((row) => {
                    const surfaceLabel = SURFACES.find((s) => s.id === row.surface)?.label ?? row.surface;
                    const hasLatency = row.avgModelLatencyMs != null && row.latencySampleCount > 0;
                    return (
                      <TableRow key={row.surface} data-testid={`surface-row-${row.surface}`}>
                        <TableCell className="text-xs font-medium">{surfaceLabel}</TableCell>
                        <TableCell className="text-xs text-right">{row.conversations.toLocaleString()}</TableCell>
                        <TableCell className="text-xs text-right">{row.escalatedCount}</TableCell>
                        <TableCell className={`text-xs text-right font-medium ${escalationRateColor(row.escalationRate)}`}>
                          {(row.escalationRate * 100).toFixed(1)}%
                        </TableCell>
                        <TableCell>
                          <EscalationReasonBadges breakdown={row.escalationReasonBreakdown} />
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono text-muted-foreground" data-testid={`tokens-in-${row.surface}`}>
                          {row.inputTokens > 0 ? row.inputTokens.toLocaleString() : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono text-muted-foreground" data-testid={`tokens-out-${row.surface}`}>
                          {row.outputTokens > 0 ? row.outputTokens.toLocaleString() : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono" data-testid={`cost-${row.surface}`}>
                          {row.ourCostUsd > 0 ? fmtUsd(row.ourCostUsd) : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono text-muted-foreground" data-testid={`latency-${row.surface}`}>
                          {hasLatency ? (
                            <span className="flex items-center justify-end gap-1" title={`${row.latencySampleCount} turn samples`}>
                              <Clock className="h-3 w-3" />
                              {row.avgModelLatencyMs! < 1000
                                ? `${row.avgModelLatencyMs}ms`
                                : `${(row.avgModelLatencyMs! / 1000).toFixed(1)}s`}
                            </span>
                          ) : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-right text-muted-foreground">
                          {row.avgTurns != null ? row.avgTurns.toFixed(1) : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Per-role breakdown ───────────────────────────────────────────────── */}
      {roleRows.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-indigo-500" />
              Escalation by Role — {RANGE_LABELS[range]}
            </CardTitle>
            <CardDescription className="text-xs">
              Escalation rate per role, so you can spot which role drives the most escalations.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Surface</TableHead>
                    <TableHead className="text-xs">Role</TableHead>
                    <TableHead className="text-xs text-right">Sessions</TableHead>
                    <TableHead className="text-xs text-right">Escalated</TableHead>
                    <TableHead className="text-xs text-right">Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roleRows.map((r) => {
                    const surfaceLabel = SURFACES.find((s) => s.id === r.surface)?.label ?? r.surface;
                    const roleLabel = ROLES.find((ro) => ro.id === r.role)?.label ?? r.role;
                    return (
                      <TableRow key={`${r.surface}-${r.role}`} data-testid={`role-row-${r.surface}-${r.role}`}>
                        <TableCell className="text-xs text-muted-foreground">{surfaceLabel}</TableCell>
                        <TableCell className="text-xs font-medium capitalize">{roleLabel}</TableCell>
                        <TableCell className="text-xs text-right">{r.conversations}</TableCell>
                        <TableCell className="text-xs text-right">{r.escalatedCount}</TableCell>
                        <TableCell className={`text-xs text-right font-medium ${escalationRateColor(r.escalationRate)}`}>
                          {(r.escalationRate * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── AI Cost Reconciliation ───────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            AI Cost Reconciliation — {RANGE_LABELS[range]}
          </CardTitle>
          <CardDescription className="text-xs">
            Sourced from <code className="font-mono bg-muted px-0.5 rounded">credit_cost_audit</code> (pool=ai), the same table as the platform margin report.
            Totals are the authoritative numbers — not a second source of truth.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {bySurfaceLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!bySurfaceLoading && bySurfaceData && (
            <>
              {/* Summary KPIs */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border bg-muted/30 p-3 text-center">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">True Cost</p>
                  <p className="text-lg font-bold text-foreground" data-testid="kpi-total-cost">
                    {fmtUsd(bySurfaceData.totalAiCostUsd)}
                  </p>
                </div>
                <div className="rounded-lg border bg-muted/30 p-3 text-center">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Revenue</p>
                  <p className="text-lg font-bold text-blue-600" data-testid="kpi-total-revenue">
                    {fmtUsd(bySurfaceData.totalAiRevenueUsd)}
                  </p>
                </div>
                <div className="rounded-lg border bg-muted/30 p-3 text-center">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Margin</p>
                  <p className={`text-lg font-bold ${bySurfaceData.marginUsd >= 0 ? "text-green-600" : "text-red-600"}`} data-testid="kpi-margin">
                    {fmtUsd(bySurfaceData.marginUsd)}
                    {bySurfaceData.marginPct != null && (
                      <span className="text-xs ml-1 font-normal">({(bySurfaceData.marginPct * 100).toFixed(1)}%)</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Breakdown by operation */}
              {bySurfaceData.costByOperation.length > 0 ? (
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Operation</TableHead>
                        <TableHead className="text-xs">Model</TableHead>
                        <TableHead className="text-xs text-right">Events</TableHead>
                        <TableHead className="text-xs text-right">Tokens In</TableHead>
                        <TableHead className="text-xs text-right">Tokens Out</TableHead>
                        <TableHead className="text-xs text-right">True Cost</TableHead>
                        <TableHead className="text-xs text-right">Revenue</TableHead>
                        <TableHead className="text-xs text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bySurfaceData.costByOperation.map((row, i) => (
                        <TableRow key={i} data-testid={`cost-row-${row.operation}`}>
                          <TableCell className="text-xs font-mono">{row.operation}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{row.modelKey ?? "—"}</TableCell>
                          <TableCell className="text-xs text-right">{row.events.toLocaleString()}</TableCell>
                          <TableCell className="text-xs text-right font-mono text-muted-foreground">
                            {row.inputTokens > 0 ? row.inputTokens.toLocaleString() : "—"}
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono text-muted-foreground">
                            {row.outputTokens > 0 ? row.outputTokens.toLocaleString() : "—"}
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono">{fmtUsd(row.ourCostUsd)}</TableCell>
                          <TableCell className="text-xs text-right font-mono text-blue-600">{fmtUsd(row.revenueUsd)}</TableCell>
                          <TableCell className={`text-xs text-right font-mono font-medium ${row.marginUsd >= 0 ? "text-green-600" : "text-red-600"}`}>
                            {fmtUsd(row.marginUsd)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No AI cost audit rows in this period.</p>
              )}

              <p className="text-[10px] text-muted-foreground leading-snug">
                <Info className="h-3 w-3 inline mr-1" />
                Per-surface cost is attributed via operation → surface mapping (e.g. <code className="font-mono bg-muted px-0.5 rounded">spend:voice_whisper_turn</code> → Clarik AI).
                Note: <code className="font-mono bg-muted px-0.5 rounded">voice_whisper_turn</code> spend covers both Clarik AI and Coach Mode since they share the same credit-spend path.
                Totals reconcile to the platform margin report.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Nightly job status + session trend */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Cpu className="h-4 w-4 text-blue-500" /> Nightly Summary Job
            </CardTitle>
          </CardHeader>
          <CardContent>
            {nightlyLastRun ? (
              <p className="text-xs text-muted-foreground">
                Last run: <span className="text-foreground font-medium">{format(new Date(nightlyLastRun), "MMM d, h:mm a")}</span>
              </p>
            ) : (
              <p className="text-xs text-amber-600">No nightly run recorded yet.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Sessions (last 7 days)</CardTitle>
          </CardHeader>
          <CardContent>
            {sessionDayTrend.length === 0 ? (
              <p className="text-xs text-muted-foreground">No session data.</p>
            ) : (
              <div className="space-y-1">
                {sessionDayTrend.map((d) => (
                  <div key={d.day} className="flex items-center gap-2 text-xs" data-testid={`trend-day-${d.day}`}>
                    <span className="w-20 text-muted-foreground font-mono">{d.day.slice(5)}</span>
                    <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${Math.min(100, (d.sessions / Math.max(...sessionDayTrend.map((x) => x.sessions), 1)) * 100)}%` }}
                      />
                    </div>
                    <span className="w-6 text-right">{d.sessions}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tool usage by role */}
      {toolUsageByRole.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Most-Used Tools by Role (30d)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {toolUsageByRole.map((roleEntry) => (
                <div key={roleEntry.role} className="space-y-1" data-testid={`tool-usage-role-${roleEntry.role}`}>
                  <p className="text-xs font-semibold capitalize text-muted-foreground">{roleEntry.role}</p>
                  {roleEntry.topTools.map((t) => (
                    <div key={t.name} className="flex items-center gap-2 text-xs">
                      <span className="flex-1 truncate font-mono text-[10px]">{t.name}</span>
                      <Badge variant="outline" className="text-xs shrink-0">{t.count}×</Badge>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tool gaps */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Cpu className="h-4 w-4 text-orange-500" /> Tool Gaps
          </CardTitle>
          <CardDescription className="text-xs">
            Patterns where users needed a tool that doesn't exist yet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(!data?.toolGaps || data.toolGaps.length === 0) && (
            <p className="text-xs text-muted-foreground">No tool gaps flagged yet.</p>
          )}
          <div className="space-y-1">
            {data?.toolGaps.map((gap: any) => (
              <div key={gap.id} className="flex items-start gap-3 px-3 py-2 rounded hover:bg-muted/50 text-xs" data-testid={`tool-gap-${gap.id}`}>
                <Badge variant="outline" className="text-xs shrink-0">{gap.frequency}×</Badge>
                <div className="flex-1">
                  <p>{gap.toolDescription}</p>
                  {gap.exampleQuery && (
                    <p className="text-muted-foreground mt-0.5">Example: "{gap.exampleQuery}"</p>
                  )}
                </div>
                {gap.resolvedAt ? (
                  <Badge className="bg-green-100 text-green-700 text-xs shrink-0">Resolved</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 text-xs shrink-0">Open</Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent review items */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Recent Review Queue Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {(!data?.recentReviewItems || data.recentReviewItems.length === 0) && (
            <p className="text-xs text-muted-foreground">No review queue activity yet.</p>
          )}
          <div className="space-y-1">
            {data?.recentReviewItems.map((item) => (
              <div key={item.id} className="flex items-center gap-3 px-3 py-2 rounded text-xs" data-testid={`analytics-review-${item.id}`}>
                <Badge className="text-xs capitalize">{item.bucket.replace(/_/g, " ")}</Badge>
                <span className="flex-1 truncate text-muted-foreground">{item.abstractedContent.slice(0, 80)}…</span>
                <span className="text-muted-foreground shrink-0">{format(new Date(item.promotedAt), "MMM d")}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Onboarding Hub Tab ────────────────────────────────────────────────────────
function OnboardingHubTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [activeStep, setActiveStep] = useState("org_name");
  const [creditGift, setCreditGift] = useState<number | null>(null);
  const [creditsPerMessage, setCreditsPerMessage] = useState<number | null>(null);
  const [savingCredits, setSavingCredits] = useState(false);

  const { data: configs } = useQuery<{ configs: HubConfig[] }>({
    queryKey: ["/api/platform/ai-hub/configs"],
  });

  // Credit settings are stored on the org_name row
  const creditRow = configs?.configs.find(
    (c) => c.surface === "onboarding" && c.role === "org_name",
  );
  const currentCreditGift = creditGift ?? creditRow?.onboardingCreditGift ?? 250;
  const currentCreditsPerMsg = creditsPerMessage ?? creditRow?.onboardingCreditsPerMessage ?? 3;

  const saveCredits = async () => {
    setSavingCredits(true);
    try {
      await apiRequest("PUT", `/api/platform/ai-hub/configs/onboarding/org_name`, {
        onboardingCreditGift: currentCreditGift,
        onboardingCreditsPerMessage: currentCreditsPerMsg,
      });
      qc.invalidateQueries({ queryKey: ["/api/platform/ai-hub/configs"] });
      setCreditGift(null);
      setCreditsPerMessage(null);
      toast({ title: "Credit settings saved" });
    } catch (err: any) {
      toast({ title: "Save failed", description: err?.message, variant: "destructive" });
    } finally {
      setSavingCredits(false);
    }
  };

  const creditsDirty = creditGift !== null || creditsPerMessage !== null;

  return (
    <div className="space-y-6">
      {/* Credit settings card — applies to all onboarding steps */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Shared Credit Pool
          </CardTitle>
          <CardDescription className="text-xs">
            All five onboarding steps draw from a single shared credit budget. These settings apply workspace-wide for new users going through the wizard.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">
                Credit Gift (total budget per wizard session)
              </Label>
              <Input
                type="number"
                value={currentCreditGift}
                onChange={(e) => setCreditGift(parseInt(e.target.value) || 250)}
                min={10}
                max={10000}
                step={10}
                className="h-8 text-sm"
                data-testid="input-onboarding-credit-gift"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">
                Credits per Message
              </Label>
              <Input
                type="number"
                value={currentCreditsPerMsg}
                onChange={(e) => setCreditsPerMessage(parseInt(e.target.value) || 3)}
                min={1}
                max={100}
                step={1}
                className="h-8 text-sm"
                data-testid="input-onboarding-credits-per-message"
              />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            With defaults (250 gift / 3 per message), users get ≈83 messages across the entire wizard.
          </p>
          {creditsDirty && (
            <Button
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={saveCredits}
              disabled={savingCredits}
              data-testid="btn-save-onboarding-credits"
            >
              {savingCredits ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save credit settings
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Per-step system prompt editor */}
      <div>
        <p className="text-sm font-medium mb-3">Step-Specific System Prompts</p>
        <Tabs value={activeStep} onValueChange={setActiveStep}>
          <TabsList className="h-8 flex-wrap">
            {ONBOARDING_STEPS.map((step) => (
              <TabsTrigger
                key={step.id}
                value={step.id}
                className="text-xs"
                data-testid={`tab-onboarding-step-${step.id}`}
              >
                {step.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="mt-4">
            {ONBOARDING_STEPS.map((step) => (
              <TabsContent key={step.id} value={step.id}>
                <ConfigurationTab surface="onboarding" lockedRole={step.id} />
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </div>

      {/* Conversations for onboarding surface */}
      <div>
        <p className="text-sm font-medium mb-3">Onboarding Conversations</p>
        <ConversationsTab surface="onboarding" />
      </div>
    </div>
  );
}

// ── Tour Strategy Tab ─────────────────────────────────────────────────────────
function TourStrategyTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ tourStrategyDoc: string }>({
    queryKey: ["/api/platform/ai-hub/tour-strategy"],
  });

  const currentDoc = draft ?? data?.tourStrategyDoc ?? "";
  const hasChanges = draft !== null;

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PUT", "/api/platform/ai-hub/tour-strategy", {
        tourStrategyDoc: currentDoc,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/ai-hub/tour-strategy"] });
      setDraft(null);
      toast({ title: "Strategy saved" });
    },
    onError: (err: any) => {
      toast({ title: "Save failed", description: err?.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-4">
      <Card className="border-blue-200 dark:border-blue-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-blue-700 dark:text-blue-300 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <MapIcon className="h-4 w-4" />
              Touring Strategy
            </span>
            <div className="flex items-center gap-2">
              {hasChanges && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setDraft(null)}
                  disabled={saveMutation.isPending}
                  data-testid="btn-reset-tour-strategy"
                >
                  <RefreshCw className="h-3 w-3 mr-1" /> Reset
                </Button>
              )}
              <Button
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !hasChanges}
                data-testid="btn-save-tour-strategy"
              >
                {saveMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Save
              </Button>
            </div>
          </CardTitle>
          <CardDescription className="text-xs">
            Write freeform touring strategy guidance here. This is injected into the Tour Planner's context on every chat turn under a &ldquo;Touring Strategy&rdquo; section. Leave blank to omit the section entirely.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <VoiceToTextTextarea
            value={currentDoc}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Describe your overall touring approach, target markets, booking priorities, seasonal strategy, preferred routing patterns, or any standing guidance the AI should factor in when helping plan tours…"
            className="font-mono text-xs min-h-[360px]"
            disabled={isLoading}
            data-testid="textarea-tour-strategy-doc"
          />
          {hasChanges && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
              <Info className="h-3 w-3" /> Unsaved changes
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Surface Tab Wrapper ──────────────────────────────────────────────────────
function SurfaceContent({ surface }: { surface: string }) {
  const [subTab, setSubTab] = useState("configuration");

  if (surface === "onboarding") {
    return <OnboardingHubTab />;
  }

  return (
    <Tabs value={subTab} onValueChange={setSubTab}>
      <TabsList className="h-8">
        <TabsTrigger value="configuration" className="text-xs" data-testid={`tab-${surface}-configuration`}>
          <Settings className="h-3.5 w-3.5 mr-1" /> Configuration
        </TabsTrigger>
        <TabsTrigger value="conversations" className="text-xs" data-testid={`tab-${surface}-conversations`}>
          <MessageSquare className="h-3.5 w-3.5 mr-1" /> Conversations
        </TabsTrigger>
        <TabsTrigger value="review-queue" className="text-xs" data-testid={`tab-${surface}-review-queue`}>
          <Flag className="h-3.5 w-3.5 mr-1" /> Review Queue
        </TabsTrigger>
        <TabsTrigger value="analytics" className="text-xs" data-testid={`tab-${surface}-analytics`}>
          <BarChart2 className="h-3.5 w-3.5 mr-1" /> Analytics
        </TabsTrigger>
        {surface === "tour" && (
          <TabsTrigger value="strategy" className="text-xs" data-testid="tab-tour-strategy">
            <MapIcon className="h-3.5 w-3.5 mr-1" /> Strategy
          </TabsTrigger>
        )}
      </TabsList>
      <div className="mt-4">
        <TabsContent value="configuration"><ConfigurationTab surface={surface} /></TabsContent>
        <TabsContent value="conversations"><ConversationsTab surface={surface} /></TabsContent>
        <TabsContent value="review-queue"><ReviewQueueTab surface={surface} /></TabsContent>
        <TabsContent value="analytics"><AnalyticsTab surface={surface} /></TabsContent>
        {surface === "tour" && (
          <TabsContent value="strategy"><TourStrategyTab /></TabsContent>
        )}
      </div>
    </Tabs>
  );
}

// ── Master Context Tab (Task #245) ───────────────────────────────────────────
interface MasterContext {
  id: string;
  voiceToneBlock: string;
  generalLibrary: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

function MasterContextTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [voiceTone, setVoiceTone] = useState<string | null>(null);
  const [library, setLibrary] = useState<string | null>(null);

  const { data, isLoading } = useQuery<MasterContext>({
    queryKey: ["/api/platform/ai-hub/master-context"],
  });

  const currentVoiceTone = voiceTone ?? data?.voiceToneBlock ?? "";
  const currentLibrary = library ?? data?.generalLibrary ?? "";
  const hasChanges = voiceTone !== null || library !== null;

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PUT", "/api/platform/ai-hub/master-context", {
        voiceToneBlock: currentVoiceTone,
        generalLibrary: currentLibrary,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/ai-hub/master-context"] });
      setVoiceTone(null);
      setLibrary(null);
      toast({ title: "Master Context saved" });
    },
    onError: (err: any) => {
      toast({ title: "Save failed", description: err?.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-5">
      <Card className="border-blue-200 dark:border-blue-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-blue-700 dark:text-blue-300 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Master Context
            </span>
            {hasChanges && (
              <Button
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                data-testid="btn-save-master-context"
              >
                {saveMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Save changes
              </Button>
            )}
          </CardTitle>
          <CardDescription className="text-xs">
            Injected into every Clarik agent (Clarik AI, Coach Mode, Tour Planner, Onboarding AI) — but not the Help bot.
            Per-surface/role configs can opt out via the &ldquo;Disable Master Context&rdquo; toggle, and role instructions still take precedence.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Voice &amp; Tone</Label>
            <VoiceToTextTextarea
              value={currentVoiceTone}
              onChange={(e) => setVoiceTone(e.target.value)}
              placeholder="Describe how every Clarik agent should sound — personality, tone, phrasing conventions…"
              className="font-mono text-xs min-h-[200px]"
              disabled={isLoading}
              data-testid="textarea-master-voice-tone"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">General Library</Label>
            <VoiceToTextTextarea
              value={currentLibrary}
              onChange={(e) => setLibrary(e.target.value)}
              placeholder="Shared facts, terminology, product context, and policies every agent should know…"
              className="font-mono text-xs min-h-[260px]"
              disabled={isLoading}
              data-testid="textarea-master-general-library"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export function AiAssistantsContent() {
  const [activeSurface, setActiveSurface] = useState("assistant");

  return (
      <div className="space-y-5">
        {/* Page header */}
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            AI Assistants Hub
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Configure, monitor, and improve Clarik AI, Coach Mode, Tour Planner, and Onboarding AI.
          </p>
        </div>

        {/* Surface tabs */}
        <Tabs value={activeSurface} onValueChange={setActiveSurface}>
          <TabsList>
            {SURFACES.map(({ id, label, icon: Icon }) => (
              <TabsTrigger key={id} value={id} className="gap-1.5" data-testid={`tab-surface-${id}`}>
                <Icon className="h-4 w-4" />
                {label}
              </TabsTrigger>
            ))}
            <TabsTrigger value="master-context" className="gap-1.5" data-testid="tab-surface-master-context">
              <Sparkles className="h-4 w-4" />
              Master Context
            </TabsTrigger>
          </TabsList>
          {SURFACES.map(({ id }) => (
            <TabsContent key={id} value={id} className="mt-4">
              <SurfaceContent surface={id} />
            </TabsContent>
          ))}
          <TabsContent value="master-context" className="mt-4">
            <MasterContextTab />
          </TabsContent>
        </Tabs>
      </div>
  );
}

export default function PlatformAiAssistants() {
  return (
    <PlatformLayout>
      <AiAssistantsContent />
    </PlatformLayout>
  );
}
